<script setup lang="ts">
import {CheckIcon} from "lucide-vue-next";
import type {Ref} from "vue";
import {onMounted, ref} from "vue";
import type {ConfigurationAdditionResponse} from "../../../responses/ConfigurationAdditionResponse.ts";
import type {ConfigurationCategoryResponse} from "../../../responses/ConfigurationCategoryResponse.ts";
import type {ConfigurationOptionResponse} from "../../../responses/ConfigurationOptionResponse.ts";
import {label} from "../../mixins/translations.ts";
import {useConfigurationStore} from "../../stores/configuration.ts";

interface Props {
    category: ConfigurationCategoryResponse
    addition: ConfigurationAdditionResponse
}

const props = defineProps<Props>();
const configurationStore = useConfigurationStore();

const selectedOption: Ref<ConfigurationOptionResponse|null> = ref(null);
const showColorPicker: Ref<boolean> = ref(true);
function selectColor(color: ConfigurationOptionResponse, update: boolean = false) {
    selectedOption.value = color;
    showColorPicker.value = false;

    if(update) {
        configurationStore.selectAddition(props.category, props.addition, color.id, true);
    }
}

onMounted(() => {
    if(configurationStore.hasAddition(props.addition.id)){
        selectColor(configurationStore.selectedAdditions[props.addition.id].option, false);
    }

    configurationStore.events.on('reset', () => {
        selectedOption.value = null;
        showColorPicker.value = true;
    });
})
</script>

<template>
    <div class="pt-3">
        <div class="flex flex-col gap-4 bg-white py-4 md:p-4 shadow-lg shadow-black/3 md:flex-row md:items-center md:justify-between">
            <button v-if="selectedOption" type="button" @click="addition.toggled = true" class="flex items-center gap-4 text-left">
                <span
                    class="inline-flex h-7 w-7 items-center justify-center rounded-full text-white"
                    :style="{ backgroundColor: selectedOption.color }">
                  <CheckIcon class="h-5 w-5" />
                </span>
                <span class="text-lg font-bold text-secondary-700">{{ selectedOption.label }}</span>
            </button>
            <span v-else class="text-lg font-bold" :class="{'text-error-700': configurationStore.validationErrors[category.id], 'text-secondary-700': !configurationStore.validationErrors[category.id]}">{{ label('site.configuration.choose_color') }}</span>

            <button
                v-if="!showColorPicker"
                type="button"
                @click="showColorPicker = true"
                class="inline-flex items-center justify-center rounded-lg border border-[#44b1a0] hover:text-white hover:bg-[#44b1a0] px-6 py-3 text-base font-medium text-[#44b1a0] transition">
                {{ label('site.configuration.choose_another_color') }}
            </button>
        </div>
        <div class="bg-white p-0 md:p-4" v-show="showColorPicker">
            <div class="grid grid-cols-8 gap-3 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-16">
                <button
                    v-for="(option, index) in addition.options"
                    :key="`${option.label}-${index}`"
                    type="button"
                    @click="selectColor(option, true)"
                    class="relative aspect-square border transition"
                    :style="{ backgroundColor: option.color }"
                    :class="selectedOption?.color === option.color ? 'border-white ring-2 ring-[#4968a9]' : 'border-transparent hover:ring-2 hover:ring-secondary-200'"
                    :aria-label="`Kies kleur ${option.label}`"
                    :title="option.label"
                >
                    <CheckIcon
                        v-if="selectedOption?.color === option.color"
                        class="absolute inset-0 m-auto h-6 w-6 text-white"
                    />
                </button>
            </div>
        </div>
    </div>

</template>
